// iter-30 — Model glossary used by the SuperAdmin LLM Routing tab AND
// mirrored in the FAQ. Single source of truth so edits sync both places.
//
// Each entry: model id → {label, provider, blurb, strengths, costs, sweet_spots, insider, stats}
// Numbers reference public list pricing (Q1 2026); update when providers move.

export const MODEL_GLOSSARY = {
  // ---------- Google Gemini ----------
  "gemini-3.1-pro-preview": {
    label: "Gemini 3.1 Pro (Preview)",
    provider: "Google Gemini",
    blurb: "Google's bleeding-edge reasoning model — preview tier means improved capabilities but occasional API hiccups.",
    strengths: "Long-context reasoning (up to 2M tokens), multimodal, strong at math + structured-output JSON.",
    costs: "$1.25 / 1M input · $10.00 / 1M output. Same as 2.5 Pro for now.",
    sweet_spots: "Tone Architect (catches subtle voice cues), Workbench heavy operations, Refinery on dense academic corpora.",
    insider: "Treat as a moving target — Google can change its ID overnight. Pin a non-preview fallback in your silo override so a 503 doesn't kill the workflow. Caching is supported but cache-creation latency is higher than 2.5 Pro.",
    stats: "8k output cap. Multimodal: text + image + audio. Caching: yes (BYOK only).",
  },
  "gemini-3-flash-preview": {
    label: "Gemini 3 Flash (Preview)",
    provider: "Google Gemini",
    blurb: "Next-gen Flash — faster and smarter than the Lite tier, still preview-tier so brace for API blips.",
    strengths: "Sub-second latency for short prompts. Excellent for compress + export_summary surfaces.",
    costs: "$0.75 / 1M input · $3.75 / 1M output (introductory — doubles on 2027-01-01). 2.5× the input cost of Flash-Lite.",
    sweet_spots: "Compress (rolling history), Export Summary, AI Assistant, Refinery on large but simple corpora where speed matters.",
    insider: "Don't use for nuanced reasoning — it'll confidently produce shallow answers. The 8k output cap rarely matters here because Flash naturally writes short.",
    stats: "8k output cap. Multimodal: text + image. Caching: yes (BYOK only).",
  },
  "gemini-2.5-pro": {
    label: "Gemini 2.5 Pro",
    provider: "Google Gemini",
    blurb: "The platform's stable workhorse. Mature, well-tested, predictable pricing. Recommended default for most surfaces.",
    strengths: "Balanced reasoning + cost. Massive context window (2M tokens). Plays nicely with Gemini explicit caching for $90% cache-hit cost reduction on the Tier-1 anchor.",
    costs: "$1.25 / 1M input · $10.00 / 1M output.",
    sweet_spots: "Public Q&A (default), Workbench, QA Probe, Refinery — the silos with stable corpora.",
    insider: "Pair with explicit context caching on the Tier-1 anchor. A creator with a 200k-char Tier-1 anchor pays ~$0.25 first call, then ~$0.025 every subsequent /ask thanks to cache hits. Without caching, every call pays full $1.25/1M input.",
    stats: "8k output cap. Multimodal: text + image. Caching: yes (BYOK only).",
  },
  "gemini-3.5-flash-lite": {
    label: "Gemini 3.5 Flash-Lite",
    provider: "Google Gemini",
    blurb: "The cheap-and-fast Gemini, and where gemini-2.5-flash now lands — Google deprecated 2.5 Flash on Dec 1 2026 and auto-redirects every request here at identical text rates.",
    strengths: "Sub-second latency, cheap, capable at summarization + extraction. Audio input is 3× cheaper than it was on 2.5 Flash.",
    costs: "$0.30 / 1M input · $2.50 / 1M output. Cache reads $0.03 / 1M.",
    sweet_spots: "Compress (rolling history), Export Summary, Auto-thank-you generation, AI Assistant. Background surfaces where users never see latency.",
    insider: "It defaults to thinking_level \"minimal\" — where 2.5 Flash reasoned automatically. This platform sets \"medium\" on Public Q&A and the AI Assistant (see llm_routing.THINKING_BY_SURFACE) because thinking tokens bill as OUTPUT, and our traffic is ~87% input: buying the reasoning back costs roughly +$1.25 per 1,000 answers, where moving to a bigger model costs ~3.4×. Sampling knobs (temperature/top_p) are deprecated on Gemini 3 — put that guidance in the system prompt instead.",
    stats: "8k output cap here (model allows 64k). Multimodal: text + image + audio + video. Caching: yes (BYOK only).",
  },

  // ---------- OpenAI ----------
  "gpt-5.2": {
    label: "GPT-5.2",
    provider: "OpenAI",
    blurb: "OpenAI's flagship reasoning model. Best for deep multi-step thinking, nuanced creative writing, and code.",
    strengths: "Top-tier reasoning, instruction-following, creative voice. Best-in-class for tasks where you need the model to 'think' before answering.",
    costs: "$5.00 / 1M input · $15.00 / 1M output. ~4× more expensive than Gemini Pro.",
    sweet_spots: "Tone Architect on subtle voices, QA Probe when you need a smart adversary, Workbench for genuinely hard reasoning tasks.",
    insider: "Don't point Public Q&A at GPT-5.2 unless your silo charges enough subscription to cover it — the math: 1k chars in + 1k chars out ≈ $0.005 per question. 1000 questions/month = $5. Same workload on Gemini Flash = $0.30. Use Boost mode for one-off heavy tasks instead.",
    stats: "16k output cap. Tools: function calling, vision, web search. Caching: not enabled on this platform.",
  },
  "gpt-5.1": {
    label: "GPT-5.1",
    provider: "OpenAI",
    blurb: "GPT-5's more stable elder sibling. Same pricing as 5.2 but with a longer track record.",
    strengths: "Like 5.2 but with marginally less aggressive reasoning. Slightly more reliable JSON output.",
    costs: "$5.00 / 1M input · $15.00 / 1M output.",
    sweet_spots: "Same as 5.2. Pick this when 5.2's preview API behavior is causing you grief.",
    insider: "If a workflow that works fine on 5.2 starts failing intermittently, demote to 5.1 first before assuming a bug.",
    stats: "16k output cap. Tools: function calling, vision. Caching: not available.",
  },
  "gpt-5": {
    label: "GPT-5",
    provider: "OpenAI",
    blurb: "Original GPT-5 release. Still available for legacy continuity.",
    strengths: "Mature reasoning. Cheaper than 5.2 in some pricing regions (not on this platform — flat $5/$15).",
    costs: "$5.00 / 1M input · $15.00 / 1M output.",
    sweet_spots: "When you need the GPT-5 family but want maximum stability and your prompts are tuned to its specific quirks.",
    insider: "Most users should pick 5.1 over 5. The output quality is marginally better and the latency is the same.",
    stats: "16k output cap. Tools: function calling, vision. Caching: not available.",
  },
  "gpt-4o": {
    label: "GPT-4o",
    provider: "OpenAI",
    blurb: "OpenAI's mid-tier multimodal model. Strong on speed, decent on reasoning, native vision support.",
    strengths: "Fast, cheaper than the GPT-5 family, multimodal-native (text + image + audio).",
    costs: "$2.50 / 1M input · $10.00 / 1M output. ~½ the cost of GPT-5.",
    sweet_spots: "QA Probe with image inputs, Workbench operations on visually-aware corpora, AI Assistant for users who prefer OpenAI's tone.",
    insider: "If your silo accepts images in user questions (Phase-3+ feature), GPT-4o's native vision is more reliable than Gemini Pro's vision until 3.1 Pro stabilizes. For text-only workloads, Gemini Pro is cheaper for the same quality.",
    stats: "16k output cap. Multimodal: text + image + audio. Caching: not available.",
  },
  "gpt-4o-mini": {
    label: "GPT-4o Mini",
    provider: "OpenAI",
    blurb: "OpenAI's budget option — cheap, fast, and competent at structured tasks.",
    strengths: "Cheap, fast, surprisingly competent at structured tasks. Same multimodal support as 4o.",
    costs: "$0.15 / 1M input · $0.60 / 1M output. Comparable to Gemini Flash.",
    sweet_spots: "Command-draft assistant, anywhere you need OpenAI's voice without the GPT-5 price tag.",
    insider: "Stateful in chat, so it holds conversational nuance well over long back-and-forth sessions — a reasonable pick if you route the AI Assistant surface here, though Gemini Flash is cheaper for high volume.",
    stats: "16k output cap. Multimodal: text + image. Caching: not available.",
  },

  // ---------- Anthropic ----------
  "claude-sonnet-4-5-20250929": {
    label: "Claude Sonnet 4.5",
    provider: "Anthropic",
    blurb: "Anthropic's smart-and-balanced flagship. Excellent at long-form reasoning, ethical refusals, and following nuanced instructions.",
    strengths: "Best-in-class for tasks where the model needs to push back on bad inputs. Strong at structured output, code, and writing in a specific voice.",
    costs: "$3.00 / 1M input · $15.00 / 1M output. Cheaper than GPT-5, more expensive than Gemini Pro.",
    sweet_spots: "Tone Architect on complex voices, Reasoning-heavy Workbench operations, Public Q&A on silos where the creator wants the model to gracefully decline out-of-scope questions.",
    insider: "Sonnet 4.5 has a famously long output context (64k). When you need a single response that lays out 50+ pages of structured analysis, Sonnet beats every Gemini and OpenAI model on the platform. Pair with the 'Reasoning Heavy' Power Mode preset.",
    stats: "64k output cap. Multimodal: text + image. Caching: not yet enabled on this platform.",
  },
  "claude-haiku-4-5-20251001": {
    label: "Claude Haiku 4.5",
    provider: "Anthropic",
    blurb: "Anthropic's fast-and-cheap option. Speed comparable to Gemini Flash, with Anthropic's signature output style.",
    strengths: "Fast, cheap-by-Anthropic-standards, retains Sonnet's gracefulness on refusals + structured output.",
    costs: "$0.80 / 1M input · $4.00 / 1M output. ~10× cheaper than Sonnet.",
    sweet_spots: "AI Assistant, compress, export_summary on creators who specifically want Anthropic-style summaries (more cautious, less likely to hallucinate facts).",
    insider: "Haiku is the platform's only sub-$5/M Anthropic option. If you have privacy-sensitive corpora (legal, medical, financial) and want Anthropic's documented training-data hygiene without the Sonnet price tag, Haiku is the move.",
    stats: "64k output cap. Multimodal: text + image. Caching: not yet enabled on this platform.",
  },
};

// Helper: list models grouped by provider for table rendering.
export function modelsByProvider() {
  const out = {};
  for (const [id, info] of Object.entries(MODEL_GLOSSARY)) {
    out[info.provider] = out[info.provider] || [];
    out[info.provider].push({ id, ...info });
  }
  return out;
}

// Render-friendly tooltip body (used by InfoTip in LLMRoutingPanel + FAQ).
export function modelTooltipBody(modelId) {
  const m = MODEL_GLOSSARY[modelId];
  if (!m) return null;
  return {
    title: `${m.label} · ${m.provider}`,
    blurb: m.blurb,
    sections: [
      { label: "Strengths", text: m.strengths },
      { label: "Pricing (per 1M tokens)", text: m.costs },
      { label: "Best for", text: m.sweet_spots },
      { label: "Insider tip", text: m.insider },
      { label: "Stats", text: m.stats },
    ],
  };
}
